import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  CHARGE_FIXED,
  CHARGE_PERCENT,
  DISCOUNT_FIXED,
  DISCOUNT_PERCENT,
} from '../discounts-charges/constants';
import type { DMS_HISTORY_STATUSES, DRIVE_TRAIN_TYPES, MpiStatus } from 'utility/constants';
import type { PaymentWritebackFailureReason } from 'payments/constants';
import type { QUOTE_APPROVAL_METHODS } from 'utility/constants/quoteApprovalMethods';
import type { ServiceGroup } from 'pages/settings/Terminals/types';

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// prettier-ignore
type LowerAlpha = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l' | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x' | 'y' | 'z';

type KebabCaseRestChar = LowerAlpha | '-' | '_' | `${number}`;

// Validate the rest of the string after the first character
type _KebabCaseRest<S extends string> = S extends ''
  ? S
  : S extends `${infer First}${infer Rest}`
    ? First extends KebabCaseRestChar
      ? _KebabCaseRest<Rest>
      : never
    : never;

// Must start with a lowercase letter, remainder may be [a-z0-9-_]
type _KebabCase<S extends string> = S extends string
  ? string extends S
    ? string // when S is the broad `string` type, allow it (no literal to validate)
    : S extends `${LowerAlpha}${infer Rest}`
      ? _KebabCaseRest<Rest> extends never
        ? never
        : S
      : never
  : never;

/** KebabCase is a type that represents a string that is in snake_case format.
 *
 * @example
 * ```typescript
* const MyFunction = <S extends string>(str: KebabCase<S>) => {
*   // ...
* };

* // Passes!
* MyFunction('this-is-valid-snake-case');

* // Passes!
* MyFunction('snake-case-can-contain-numbers-123');

* // Fails! KebabCase does not allow special characters
* MyFunction('this-i$-not-valid-snake-case');

* // Fails! KebabCase does not allow uppercase letters
* MyFunction('this-is-not-valid-Snake-case');

* // Fails! KebabCase must start with a lowercase letter
* MyFunction('0-this-is-not-valid-snake-case');
* ```
*/
export type KebabCase<S extends string = string> = _KebabCase<S> & { __brand?: 'KebabCase' };

export type FactoryMaintenanceEstimatePart = {
  applicationId: string;
  manufacturer: string;
  name: string;
  partNumber: string;
  position: string;
  price: number;
  qualifiers: [];
  quantity: number;
};

export type FactoryMaintenanceFluid = {
  fluidId: number;
  partTerminologyName: string;
  unitOfMeasure: string;
  viscosity: string;
  formulation: string;
  maxCapacity: number;
  capacity: number | null;
  unitPrice: number | null;
};

export type FluidPricing = {
  id: number;
  fluidType: string;
  formulation: string | null;
  viscosity: string | null;
  unitOfMeasure: string;
  unitPrice: string | null;
  dmsPartNumber: string | null;
  dmsPartName: string | null;
  dmsPartId: number | null;
  motorFluidId: number | null;
};

type FactoryMaintenanceValueProposition = {
  maintenanceItemName: string;
  pitch: string | null;
};

export type FactoryMaintenanceEstimate = {
  AdditionalLaborTime: null;
  AdditionalLaborTimeDescription: null;
  AdditionalWarrantyLaborTime: null;
  AllLaborTime: null;
  AllLaborTimeDescription: null;
  AllWarrantyLaborTime: null;
  BaseLaborTime: number;
  BaseLaborTimeAverage: null;
  BaseLaborTimeDescription: null;
  BaseWarrantyLaborTime: null;
  EstimatedWorkTimeID: null;
  IsActive: null;
  LaborTimeInterval: null;
  Notes: { Text: null };
  RequiredSkill: { Description: string };
  ServiceType: string;
  Type: null;
  applicationId: number;
  frequencyType: string;
  intervalMiles: number;
  intervalMonths: number;
  name: string;
  parts: FactoryMaintenanceEstimatePart[];
  fluids: FactoryMaintenanceFluid[];
  severeCondition: boolean;
  valueProposition?: FactoryMaintenanceValueProposition | null;
};

type FactoryMaintenanceTotals = {
  laborCost: number;
  laborHours: number;
  partsCost: number;
  fluidsCost: number;
  totalCost: number;
};

export type FactoryMaintenanceSchedule = {
  id: number;
  estimates: FactoryMaintenanceEstimate[];
  frequencyType: string;
  intervalMiles: number;
  intervalMonths: number;
  name: string;
  severeCondition: boolean;
  serviceMoreFrequently: boolean;
  totals: FactoryMaintenanceTotals;
  factoryMaintenanceMenuId: number | null;
  laborCost: string | null;
  partsCost: string | null;
  isCustomLaborPrice: boolean;
};

type VehicleGroupLink = {
  Href: string;
  Rel: string;
};

type DriveTypeId = number;
export type DriveTrain = (typeof DRIVE_TRAIN_TYPES)[keyof typeof DRIVE_TRAIN_TYPES];

export type DriveTypeData = {
  DriveTypeID: DriveTypeId;
  Type: DriveTrain;
};

export type VehicleGroup = {
  BaseVehicleID: string;
  DriveTypeInfo?: DriveTypeData;
  EngineDescription: string;
  EngineID: string;
  FactoryMaintenanceSchedules: FactoryMaintenanceSchedule[];
  GroupName: string;
  Links: VehicleGroupLink[];
  MakeID: string;
  MakeName: string;
  ModelID: string;
  ModelName: string;
  SubModelID: string;
  SubModelModel: string;
  VehicleID: string;
  Year: string;
};

export type FactoryMaintenanceMenuApiResponse = {
  vehicleGroups: VehicleGroup[];
};

export type Transmission = {
  TransmissionID: number;
  Type: string | null;
  ControlType: string | null;
  Speed: string | null;
  ManufacturerCode: string | null;
  ElectronicControl: string | null;
};

export type MotorResolveVinApiResponse = {
  requiresTransmissionSelection: boolean;
  transmissions: Transmission[];
  storedTransmissionId: string | null;
  vehicle: VehicleGroup;
};

export type DiscountType = typeof DISCOUNT_FIXED | typeof DISCOUNT_PERCENT;
export type ChargeType = typeof CHARGE_FIXED | typeof CHARGE_PERCENT;

export type CachedWithMeta = {
  data: AxiosResponse;
  timestamp: number;
};

export type CachedResponse = {
  cache: CachedWithMeta['data'];
  fromCache: boolean;
};

export type GetWithCacheOptions = AxiosRequestConfig & {
  transform?: (data: Awaited<AxiosResponse>) => any;
  resetCache?: boolean;
  ttlMs?: number;
};

export type ExtendedAxiosInstance = {
  getWithCache(url: string, options?: GetWithCacheOptions): Promise<CachedResponse>;
  postJson<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<R>;
  putJson<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<R>;
  patchJson<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<R>;
} & AxiosInstance;

export const APPOINTMENT_PDF_SIGNATURE_DOC_TYPE = {
  APPOINTMENT: 'workOrder',
  WALK_IN: 'workOrder',
  CHECK_OUT: 'invoice',
} as const;

export type PDFSignatureDocType =
  (typeof APPOINTMENT_PDF_SIGNATURE_DOC_TYPE)[keyof typeof APPOINTMENT_PDF_SIGNATURE_DOC_TYPE];

export const PARTS_EXPRESS_TASK_TYPE = {
  APPROVED_RECOMMENDATION: 'APPROVED_RECOMMENDATION',
  RECOMMENDATION: 'RECOMMENDATION',
  PRE_APPROVAL: 'PRE_APPROVAL',
} as const;

// ISA-5087: who the parts advisor hands the quote to when finishing a task.
export const PARTS_EXPRESS_FINISH_RECIPIENT = {
  ADVISOR: 'advisor',
  TECHNICIAN: 'technician',
} as const;

export type PartsExpressFinishRecipient =
  (typeof PARTS_EXPRESS_FINISH_RECIPIENT)[keyof typeof PARTS_EXPRESS_FINISH_RECIPIENT];

export const PARTS_EXPRESS_TASK_STATUS = {
  IN_PROGRESS: 'In Progress',
  NOT_STARTED: 'Not Started',
} as const;

type TaskType = (typeof PARTS_EXPRESS_TASK_TYPE)[keyof typeof PARTS_EXPRESS_TASK_TYPE];

export type PartsExpressTask = {
  assignedTo: User | null;
  assignedToMe: boolean;
  customer: PartsExpressRepairOrderApiRes['primaryCustomer'];
  id: number;
  roNumber: string;
  taskJobs: PartsExpressTaskJobApiRes[] | undefined;
  taskLoading: boolean;
  taskStarted: boolean;
  taskType: TaskType;
  taskTimeElapsed: number;
  title: string;
  isPreApprovalTask: boolean;
  isApprovedRecommendationTask: boolean;
  isRecommendationTask: boolean;
  actionErrorMessage: string | undefined;
  vehicleInfo: {
    year: PartsExpressRepairOrderApiRes['year'];
    make: PartsExpressRepairOrderApiRes['make'];
    model: PartsExpressRepairOrderApiRes['model'];
    miles: PartsExpressRepairOrderApiRes['miles'];
    vin: PartsExpressRepairOrderApiRes['vin'];
  };
};

export type PartsExpressTaskJobApiRes = {
  id: number;
  operationCode: string;
  description: number | null;
  preApproved: boolean;
  approved: boolean | null;
  notes: string | null;
  jobType: string;
  complaint: string;
  dmsJobNumber: string | null;
};

type PartsExpressRepairOrderApiRes = {
  id: number;
  primaryPartsAdvisor: User | null;
  primaryCustomer: Customer | null;
  number: string;
  year: number;
  make: string;
  model: string;
  miles: number | null;
  vin: string;
};

export type PartsExpressTasksApiRawRes = {
  results: {
    id: number;
    type: keyof typeof PARTS_EXPRESS_TASK_TYPE;
    date: string;
    status: (typeof PARTS_EXPRESS_TASK_STATUS)[keyof typeof PARTS_EXPRESS_TASK_STATUS];
    statusTimestamp: string;
    repairOrder: PartsExpressRepairOrderApiRes;
    recommendations: PartsExpressTaskJobApiRes[];
    assignedTo: User | null;
  }[];
};

export type PartsExpressTasksApiResults = PartsExpressTasksApiRawRes['results'];
export type PartsExpressTasksApiItem = PartsExpressTasksApiResults[number];

export enum LyWindow {
  JOBS = 'jobs',
  ESTIMATOR = 'estimator',
}

export type SignablePdfData = {
  base64: string;
  signatureCoordinates: {
    left: number;
    top: number;
    height: number;
    width: number;
    page: number;
  }[];
};

export type NonEmptyString<T> = T extends '' ? never : T;

export type ApiResponse<T> = {
  results: T[];
  totalRefunded: number;
  totalResults: number;
  totalPages: number;
  previous: string;
  currentPage: number;
  next: string;
};

// a walk-in is a repair order without an appointment
export type WalkIn = Omit<RepairOrder, 'appointments'> & { isWalkIn: true; appointments: [] };

export type Appointment = {
  id: number;
  guestUser: GuestUser | null;
  user: User | null;
  customer: Customer;
  alternativeTransportationType: TransportationType | null;
  repairOrder: RepairOrder | null;
  dateCreated: Date;
  date: string;
  textUpdates: boolean;
  emailUpdates: boolean;
  vehicleYear: number;
  vehicleModel: string;
  vehicleMake: string;
  miles: number | null;
  vin: string | null;
  complaint: string | null;
  internalNotes: string | null;
  deleted: boolean;
  appointmentServices: AppointmentService[];
  otherRequest: string | null;
  dmsId: string | null;
  vehicleDmsId: string | null;
  source?: 'DMS' | 'Member' | 'Customer'; // set by API
  servicesInformation: AppointmentService[];
  isReadOnly: boolean;
};

// ISA-5112: recallNumber, description and status come from CarFax only; NHTSA recalls carry null.
export type Recall = {
  component: string | null;
  consequence: string | null;
  defaultOperationCode: OperationCode;
  description: string | null;
  manufacturer: string | null;
  nhtsaNumber: string;
  operationCodes: OperationCode[];
  recallNumber: string | null;
  remedy: string | null;
  reportDate: string | null;
  status: string | null;
  summary: string | null;
  unitsEffected: number | null;
};

export type Recalls = Recall[];

type RecallSource = 'carfax' | 'nhtsa';

// Why the recall list is what it is. Anything but 'ok' comes with an empty list.
export type RecallLookupStatus = 'ok' | 'vin-not-decoded' | 'model-not-matched' | 'unavailable';

// The decoded vehicle NHTSA was searched for (vPIC's make/model spelling).
export type RecallVehicle = {
  year: number;
  make: string;
  model: string;
};

// GET /api/recalls response (iservice-api RecallLookupResult).
export type RecallLookupResponse = {
  source: RecallSource;
  lookupStatus: RecallLookupStatus;
  // True only when the dealer's CarFax location ID was sent but CarFax failed or rejected it, so the
  // list fell back to NHTSA.
  carfaxFailed: boolean;
  vehicle: RecallVehicle | null;
  recalls: Recalls;
};

// What the lookup said about its list, without the list itself.
export type RecallLookup = Omit<RecallLookupResponse, 'recalls'>;

export type DeclinedServicesResponse = {
  advisor: string | null;
  closedDate: string | null;
  deferrals: {
    operationCode: OperationCode;
    recommendation: RepairOrderQuoteRecommendation;
    parts: Part[] | undefined;
  }[];
  repairOrderNumber: string;
  technician: string | null;
};

export enum ServiceLanes {
  body = 'body',
  express = 'express',
  main = 'main',
  none = 'none',
}

export type AppointmentCode = {
  id: number;
  appointmentCodeGroup: AppointmentCodeGroup;
  appointmentCodeGroupId?: number;
  code: string;
  complaint: string | null;
  cost: number;
  customerHours: number;
  deleted: boolean | null;
  internal: boolean;
  laborHours: number;
  maxAllowedPerDay?: number | null;
  serviceGroup: ServiceGroup | null;
  serviceGroupId?: number | null;
  serviceLane: string;
  sortOrder: number | null;
  title: string;
};

export type AppointmentCodeServicePayload = Partial<AppointmentService>;

export type AppointmentService = {
  type: 'operationCode' | 'appointmentCode';
  code: string;
  complaint: string | null;
  description: string;
  dmsId: number | null;
  hours: string;
  id: number;
  internalNotes: string | null;
  serviceLane: ServiceLanes;
};

export type AppointmentCodeGroup = {
  id: number;
  name: string;
  sortOrder: number;
  appointmentCodes: AppointmentCode[] | null;
  isDefault: boolean;
};

export type Customer = {
  id: number;
  name: string;
  phone: string | null;
  mobileConfirmed: boolean;
  email: string | null;
  doNotContact: boolean;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  customerRewards: CustomerRewards;
  marketingOptOut: boolean;
  customerVehicles: CustomerVehicle[] | null;
  dateLastContacted: Date | null;
  dateLastVisited: Date | null;
  unread: number;
  dmsId: string | null;
  serviceSmsOptedOut: boolean;
  marketingSmsOptedOut: boolean;
};

/** Denotes a customer as found in DMS */
export type DmsCustomer = Omit<Customer, 'dmsId'> & {
  dmsId: string;
};

export type CustomerVehicle = {
  id: number;
  year: number;
  make: string;
  model: string;
  miles: number | null;
  vin: string | null;
  licensePlate?: {
    id: number;
    plateNumber: string;
    state: string;
  };
  customer: Customer;
};

export type CustomerRewards = {
  dateFirstEarned: string;
  dateLastEarned: string;
  points: number;
  totalEarned: number;
  totalSpent: number;
  rewards: number;
};

export type DropdownOption<T = unknown> = {
  label: string;
  value: T;
};

type GuestUser = {
  id: number;
  dateCreated: Date | null;
  totalRequestCount: number | null;
  updateRequestCount: number | null;
  appointments: Appointment[];
};

export type InternalMessage = {
  id: number;
  from: User;
  to: User;
  message: string;
  date: Date;
  isRead: boolean;
};

export type PaginatedResponse = {
  currentPage: number;
  next: string | null;
  previous: string | null;
  totalPages: number;
  totalResults: number;
};

// Derived charge display for DealerBuilt pay types (ISA-4671). Present on a labor rate only when the
// DealerBuilt pay-type derivation governs its charges, in which case No Charge / Supplies are
// computed from the pay type rather than the manual settings and shown read-only.
export type DmsPayTypeCharges = {
  payType: string;
  noCharge: boolean;
  noChargeContextual: boolean;
  suppliesLocked: boolean;
};

export type LaborRate = {
  id: number;
  name: string;
  rate: string;
  code: string;
  useMatrix: boolean;
  calculateSupplies: boolean;
  laborRateMatrix: LaborRateMatrix | null;
  defaultNoCharge: boolean;
  dmsCode: string | null;
  laborPriceCode: string | null;
  dmsPayTypeCharges?: DmsPayTypeCharges | null;
};

export type LaborRateResponse = {
  results: LaborRate[];
} & PaginatedResponse;

export type OpCodesSettingsResponse = {
  results: OperationCode[];
  // What each result adds to a quote as a Dealership Package line, by Op Code id. Only sent when
  // the list is fetched with includePackagePrice.
  packagePrices?: Record<number, number>;
} & PaginatedResponse;

type LaborRateMatrix = {
  id: number;
};

export type LaborRateMatrixValue = {
  id: number;
  hours: number;
  price: number | null;
};

type LyMoney = {
  amount: number;
  currency: string;
};

type LyStreetAddress = {
  city: string;
  country: string;
  county: string;
  line1: string;
  line2: string;
  state: string;
  zip: string;
};

type LyHumanName = {
  firstName: string;
  lastName: string;
  middleName: string;
  singularName: string;
  suffix: string;
  title: string;
};

type LyManagerUser = {
  eventDescription: string;
  eventID: unknown | null;
  eventOccurrenceDateTime: unknown | null;
  factoryID: unknown | null;
  isActive: boolean;
  number: string;
  otherId: unknown | null;
  personalName: LyHumanName;
  specialRemarksDescription: string;
  ssn: string;
  streetAddress: LyStreetAddress;
  username: unknown | null;
};

// TODO no idea what's nullable
export type LyFinanceInvoice = {
  attributes: {
    advertisingSource: string;
    advertisingSourceCode: string;
    basePaymentAmount: LyMoney;
    baseVehicleGross: LyMoney;
    buy: {
      amountFinanced: LyMoney;
      balloonRate: number;
      beaconScore: number;
      deferredDownPayments: unknown[];
      deliveredPrice: LyMoney;
      deposit: LyMoney;
      fees: {
        bank: {
          fee: LyMoney;
        };
        dealerFees: unknown[];
        licensing: {
          fee: LyMoney;
        };
        loanOrigination: {
          fee: LyMoney;
        };
        loanProcessing: {
          fee: LyMoney;
        };
        proratedLicensing: {
          fee: LyMoney;
        };
        tradeLicensing: {
          fee: LyMoney;
        };
      };
      finalPayment: LyMoney;
      financeCharge: LyMoney;
      lsiVsi: LyMoney;
      paymentDescription: unknown | null;
      taxes: {
        dealerTaxes: unknown[];
      };
      totalAmountFinanced: LyMoney;
      tradeTaxCredit: LyMoney;
    };
    buyRate: number;
    cashDownPayment: LyMoney;
    city: string;
    cobuyer: {
      identity: {
        address1: string;
        address2: string;
        birthDate: unknown | null;
        city: string;
        citySubDivisionName: unknown | null;
        country: string;
        county: string;
        emailAddress: string;
        personalName: LyHumanName;
        phoneNumbers: unknown[];
        sex: string;
        spouseAddress1: string;
        spouseAddress2: string;
        spouseCity: string;
        spouseCountry: unknown | null;
        spouseCounty: string;
        spouseEmailAddress: string;
        spouseName: LyHumanName;
        spousePhoneNumbers: unknown[];
        spouseSsn: string;
        spouseState: string;
        spouseZip: string;
        ssn: unknown | null;
        state: string;
        zip: string;
      };
      isMarried: boolean;
      license: {
        number: string;
        state: string;
      };
      relationshipType: string;
    };
    contractDate: string;
    costAdds: unknown[];
    customerDriverLicense: {
      number: string;
      state: string;
    };
    customerType: string;
    customerTypeDetail: '-';
    dateApproved: unknown | null;
    dateCheckedOut: unknown | null;
    dateProcessed: unknown | null;
    dateSentToAccounting: string;
    dateSold: string;
    daysToFirstPayment: number;
    dealNumber: number;
    dealPurchaseProgram: string;
    dealStatus: string;
    dealStatusList: unknown | null;
    dealType: string;
    dealerRebateAmount: LyMoney;
    dealerState: string;
    dealerZip: string;
    decrLifePremium: LyMoney;
    discountOtherTotal: LyMoney;
    downPayment: LyMoney;
    eventOccurrenceDateTime: string;
    feeAmount: LyMoney;
    finalAmount: LyMoney;
    financeCharge: LyMoney;
    financeChargeAmount: LyMoney;
    financeDeptGross: LyMoney;
    financeDeptGrossWithHoldback: LyMoney;
    financeManagers: LyManagerUser[];
    financeReserve: LyMoney;
    firstPaymentDate: string;
    fleetAccountString: string;
    grossSellingPrice: LyMoney;
    hardAdds: unknown[];
    hardAddsTotal: LyMoney;
    incentives: unknown[];
    incentivesTotal: LyMoney;
    insuranceProducts: unknown[];
    interestDays: number;
    isApproved: boolean;
    isCheckedOut: boolean;
    isProcessed: boolean;
    isSentToAccounting: boolean;
    isSold: boolean;
    lease: {
      baseResidualAmount: LyMoney;
      capCostReduction: LyMoney;
      cashCapReductionAmount: LyMoney;
      cashRequired: LyMoney;
      estimatedOfficialFeeTax: LyMoney;
      factorInterestType: unknown | null;
      feeAmount: LyMoney;
      fees: {
        acquisition: {
          __value: unknown | null;
        };
        dealerFees: unknown[];
        disposition: {
          fee: LyMoney;
        };
        licensing: {
          fee: LyMoney;
          feeType: string;
        };
        proratedLicensing: {
          fee: LyMoney;
          feeType: string;
        };
        tradeLicensing: {
          fee: LyMoney;
          feeType: string;
        };
      };
      grossCapCost: LyMoney;
      hardAddsResidualAmount: LyMoney;
      hardAddsResidualPercentage: number;
      isOnePayLease: boolean;
      mileageActual: number;
      mileageAllowance: number;
      mileageDollarsPerMileLeaseEnd: LyMoney;
      mileageDollarsPerMileUpFront: LyMoney;
      mileageEstimate: number;
      milesUpFront: number;
      moneyFactor: number;
      monthlyDepreciationAmount: LyMoney;
      monthlyServiceCharge: LyMoney;
      netCapCost: LyMoney;
      otherMonthlyCharges: LyMoney;
      packageDiscounts: LyMoney;
      rebateCapReductionAmount: LyMoney;
      residualAmount: LyMoney;
      residualPercentage: number;
      securityDeposit: LyMoney;
      taxes: {
        dealerTaxes: unknown[];
        monthlyUse: LyMoney;
        property: LyMoney;
      };
      totalDueAtSigning: LyMoney;
      tracLease: boolean;
      tradeTaxCredit: LyMoney;
    };
    leasePrice: LyMoney;
    leaseRateMoneyFactorString: string;
    lenderAddress: string;
    lenderCity: string;
    lenderCode: string;
    lenderDealerNumber: string;
    lenderFedSSN: string;
    lenderName: string;
    lenderPhone: {
      digits: string;
      numberType: string;
    };
    lenderRemitTileName: unknown | null;
    lenderRemitTitleAddress: string;
    lenderRemitTitleCity: string;
    lenderRemitTitleName: string;
    lenderRemitTitleState: string;
    lenderRemitTitleZip: string;
    lenderState: string;
    lenderZip: string;
    manufacturerRebateAmount: LyMoney;
    maturityDateYearMonthDate: string;
    milesAtSaleDate: number;
    mrm: LyMoney;
    msrp: LyMoney;
    netAmountFinanced: LyMoney;
    netAnnualPercentageRate: number;
    netCapitalizedCostAmount: LyMoney;
    netTradeAllowance: LyMoney;
    netTradeAllowanceAmount: LyMoney;
    numberOfPaymentsNumeric: number;
    otherDeptGross: LyMoney;
    otherDeptGrossWithHoldback: LyMoney;
    paymentCount: number;
    prepaidFinanceChargeAmount: LyMoney;
    productDetails: unknown[];
    protectionPackages: unknown[];
    purchasePriceAmount: LyMoney;
    rate: number;
    rebates: unknown[];
    rebatesTotal: LyMoney;
    regularPayment: LyMoney;
    residualAmount: LyMoney;
    saleCategoryString: string;
    salesDeptGross: LyMoney;
    salesDeptGrossWithHoldback: LyMoney;
    salesGeneralManager: unknown[];
    salesManager: LyManagerUser;
    salesNumber: string;
    salesNumber2: unknown | null;
    salesPeople: unknown[];
    securityDepositAmount: LyMoney;
    sellingPrice: LyMoney;
    serviceContracts: unknown[];
    softAdds: unknown[];
    taxAmount: LyMoney;
    taxableSalesAmount: LyMoney;
    taxes: unknown[];
    temporaryTag: string;
    termMonths: number;
    totalDownPayment: LyMoney;
    totalOfPayments: LyMoney;
    totalReductionsAmount: LyMoney;
    totalTradeAllowance: LyMoney;
    transactionPrice: LyMoney;
    updateStamp: string;
    upsource: string;
    vehicleType: string;
  };
  dealKey: string;
  dealNumber: number;
  placement: {
    groupId: number;
    groupName: string;
    environmentId: number;
    environmentType: string;
    dealerId: number;
    sourceId: number;
    companyId: number;
    companyName: string;
    storeId: number;
    storeName: string;
  };
  references: {
    customerKey: string;
    inventoryKey: string;
    tradeIns: any[];
  };
};

export type LyPartsInvoice = {
  placement: {
    groupId: number;
    groupName: string;
    environmentId: number;
    environmentType: string;
    dealerId: number;
    sourceId: number;
    companyId: number;
    companyName: string;
    storeId: number;
    storeName: string;
    serviceLocationId: number;
    serviceLocationName: string;
  };
  attributes: {
    accountingDate: string;
    actualWholesaleAmount: LyMoney;
    address: string;
    balanceDue: LyMoney;
    city: string;
    closeDate: string;
    comments: string;
    coreAmount: LyMoney;
    cost: LyMoney;
    countyTaxAmount: LyMoney;
    customerFocusIncentives: unknown | null;
    customerPurchaseOrderNumber: string;
    dealerDiscountAmount: LyMoney;
    dealerNumber: unknown | null;
    deliveredDate: unknown | null;
    deliveryType: string;
    documentDate: string;
    documentID: string;
    footerText: unknown | null;
    freightCharge: LyMoney;
    freightTerms: unknown | null;
    headerText: unknown | null;
    invoiceDate: string;
    invoiceNumber: string;
    invoiceTask: unknown | null;
    invoiceType: unknown | null;
    isCod: boolean;
    isDelivered: boolean;
    isDelivery: boolean;
    isReturn: boolean;
    isTaxable: boolean;
    isWholesale: boolean;
    legalName: string;
    letterOfCredit: unknown | null;
    locationName: string;
    miscChargeTotal: LyMoney;
    modifiedDate: string;
    monthlyDiscountsOrAllowancesAmount: LyMoney;
    monthlyMiscSalesChargesAmount: LyMoney;
    monthlyPartsTotalAmount: LyMoney;
    monthlyTotalAmount: LyMoney;
    partNumbersStocked: unknown | null;
    partsDiscountAmount: LyMoney;
    partsInvoiceItemAmount: LyMoney;
    partsInvoiceLines: {
      actualWholesaleAmount: LyMoney;
      binLocation: string;
      caseCartonNumber: unknown | null;
      comments: string;
      conveyanceNumber: unknown | null;
      coreAmount: LyMoney;
      coreUnitAmount: LyMoney;
      dealerDiscountAmount: LyMoney;
      detailNotes: unknown | null;
      discountPercentage: unknown | null;
      eTADate: unknown | null;
      extendedAmount: LyMoney;
      extendedCoreAmount: LyMoney;
      freightCharge: number;
      grossDiscountAmount: LyMoney;
      hTSCode: unknown | null;
      headerNotes: unknown | null;
      linenumber: number;
      manufacturerOrderNumber: unknown | null;
      miscChargeTotal: LyMoney;
      miscDescription: unknown | null;
      orderDate: unknown | null;
      orderReferenceNumber: unknown | null;
      orderType: unknown | null;
      packQty: number;
      partClass: string;
      partKey: string;
      partName: string;
      partNumber: string;
      partsInvoiceItemNetAmount: LyMoney;
      priceDescription: string;
      processCode: unknown | null;
      processDate: unknown | null;
      purchaseOrderNum: string;
      quantityOrdered: number;
      quantityShipped: number;
      retailAmount: LyMoney;
      saleType: string;
      shipmentNumber: unknown | null;
      shippingPDC: unknown | null;
      soldAmount: LyMoney;
      soldDate: string;
      source: string;
      sourceDescription: string;
      specialTax: number;
      submittedAmount: LyMoney;
      taxAmount: LyMoney;
      taxDescription: string;
      taxPaidToName: string;
      tradeInAmount: LyMoney;
      vendor: string;
    }[];
    purchaseOrderNumber: string;
    referenceNumber: string;
    restockingFee: LyMoney;
    secondaryDealerNumber: unknown | null;
    shipMethod: string;
    shipTo: {
      address1: string;
      address2: string;
      birthDate: unknown | null;
      city: string;
      citySubDivisionName: unknown | null;
      country: string;
      county: string;
      emailAddress: string;
      personalName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: string;
        suffix: string;
        title: string;
      };
      phoneNumbers: unknown[];
      sex: string;
      spouseAddress1: string;
      spouseAddress2: string;
      spouseCity: string;
      spouseCountry: unknown | null;
      spouseCounty: string;
      spouseEmailAddress: string;
      spouseName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: string;
        suffix: string;
        title: string;
      };
      spousePhoneNumbers: unknown[];
      spouseSsn: string;
      spouseState: string;
      spouseZip: string;
      ssn: string;
      state: string;
      zip: string;
    };
    shipToLocationID: unknown | null;
    shipmentCarrierDetails: unknown[];
    shipmentNumber: string;
    soldBy: {
      eventDescription: string;
      eventID: unknown | null;
      eventOccurrenceDateTime: unknown | null;
      factoryID: unknown | null;
      isActive: boolean;
      number: string;
      otherId: unknown | null;
      personalName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: unknown | null;
        suffix: string;
        title: string;
      };
      specialRemarksDescription: string;
      ssn: string;
      streetAddress: {
        city: string;
        country: string;
        county: string;
        line1: string;
        line2: string;
        state: string;
        zip: string;
      };
      username: string;
    };
    state: string;
    status: number;
    subHeaderText: unknown | null;
    subTotal: LyMoney;
    taxDescription: string;
    taxOnTradeInTaxAmount: LyMoney;
    taxPercent: number;
    titleText: unknown | null;
    totalAmount: LyMoney;
    totalDiscountAmount: LyMoney;
    totalOtherAmount: LyMoney;
    totalQuantity: unknown | null;
    totalTaxAmount: LyMoney;
    tradeInAmount: LyMoney;
    yTDDiscountsOrAllowancesAmount: LyMoney;
    yTDMiscSalesChargesAmount: LyMoney;
    yTDPartsTotalAmount: LyMoney;
    yTDTotalAmount: LyMoney;
    zipcode: string;
  };
  partsInvoiceKey: string;
  references: {
    payments: {
      amount: LyMoney;
      createdStamp: string;
      description: string;
      glAccount: string;
      includedInBalance: boolean;
      payType: string;
      paymentKey: string;
      paymentMethodCode: string;
      username: string;
    }[];
    soldToCustomer: {
      placement: {
        groupId: number;
        groupName: string;
        environmentId: number;
        environmentType: string;
        dealerId: number;
        sourceId: number;
      };
      attributes: {
        allowsEmailContact: boolean;
        allowsMailContact: boolean;
        allowsPhoneContact: boolean;
        allowsSmsContact: boolean;
        comments: string;
        customerNumber: string;
        customerType: string;
        driverLicenseNumber: string;
        driverLicenseState: string;
        identity: {
          address1: string;
          address2: string;
          birthDate: string;
          city: string;
          citySubDivisionName: unknown | null;
          country: string;
          county: string;
          emailAddress: string;
          personalName: {
            firstName: string;
            lastName: string;
            middleName: string;
            singularName: unknown | null;
            suffix: string;
            title: string;
          };
          phoneNumbers: {
            digits: string;
            numberType: string;
          }[];
          sex: string;
          spouseAddress1: string;
          spouseAddress2: string;
          spouseCity: string;
          spouseCountry: unknown | null;
          spouseCounty: string;
          spouseEmailAddress: string;
          spouseName: {
            firstName: string;
            lastName: string;
            middleName: string;
            singularName: unknown | null;
            suffix: string;
            title: string;
          };
          spousePhoneNumbers: {
            digits: string;
            numberType: string;
          }[];
          spouseSsn: string;
          spouseState: string;
          spouseZip: string;
          ssn: string;
          state: string;
          zip: string;
        };
        isDealerToDealer: boolean;
        isMarried: boolean;
        isWholesale: boolean;
        lastContactDate: string;
        modifiedStamp: string;
        spouseName: unknown | null;
      };
      customerId: string;
      customerKey: string;
    };
  };
};

export type MotorEstimate = any;

export type MpiTemplate = {
  id?: number;
  name: string;
  active?: boolean;
  mpiGroups: MpiGroup[];
  presetName: string | null;
};

export type MpiTemplates = MpiTemplate[];

export type MpiGroup = {
  id?: number;
  name: string;
  active?: boolean;
  mpiItems: MpiItem[];
};

export type MpiItem = {
  id?: number;
  mpiGroup?: MpiGroup;
  name: string;
  rangeMaximum?: number | null;
  rangeUnit?: string | null;
  value?: number | null;
  status?: MpiResultsStatus;
};

export type MpiItemOperationCode = {
  id: number;
  mpiItem: MpiItem;
  operationCode: OperationCode;
};

export const OPERATION_CODE_CATEGORY = {
  PARTS: 'parts',
  SERVICE: 'service',
} as const;

type OperationCodeCategory = (typeof OPERATION_CODE_CATEGORY)[keyof typeof OPERATION_CODE_CATEGORY];

export type OperationCode = {
  id: number;
  category: OperationCodeCategory | null; // only used for Goodyear ISA-2166
  cause: string | null;
  chargeSupplies: boolean;
  code: string;
  complaint: string | null;
  correction: string | null;
  customerNote?: string | null; // nhtsa consequence op code maping
  defaultLaborRate: LaborRate | null; // get
  defaultLaborRateId?: number; // create and update
  dmsId: string | null;
  laborHours: number;
  // decimal columns serialize as strings, so coerce with Number() before doing arithmetic
  laborPrice: number | string;
  laborPriceCode: string | null;
  laborTaxable: boolean;
  menuPartsPricing: boolean | null;
  menuPricing: boolean | null;
  partsPrice: number;
  partsTaxable: boolean;
  serviceGroup: ServiceGroup | null;
  serviceGroupId: number | null;
  serviceLane: ServiceLanes;
  usedCount: number;
  activeInIservice: boolean;
  readOnlyFields: ReadonlyArray<string>;
  // ISA-2074 Story 1.4: last-observed Lightyear dispatch skill for this Op Code, harvested from RO
  // pulls. Read-only — null when the flag is off or the Op Code has never been observed with a skill.
  observedSkill?: string | null;
};

export type Part = {
  // because Reasons™
  part: Part;

  id: number;
  number: string | null;
  name: string;
  bin: string | null;
  available: number | null;
  price: number | null;
  replacesPartNumber: string | null;
  cost: string | null;
  list: string | null;
  trade: string | null;
  comp: string | null;
  exchange: string | null;
  price6: string | null;
  price7: string | null;
  price8: string | null;
  price9: string | null;
  price10: string | null;
  unit: string | null;
  core: string | null;
  retail: string | null;
  source: string | null;
  manufacturerCode: string | null;
  usedCount: number;
  vendor: string | null;
};

type PartMatrix = {
  id: number;
  name: string;
  partMatrixValues: PartMatrixValue[] | null;
};

type PartMatrixValue = {
  id: number;
  partMatrx: PartMatrix;
  min: string;
  max: string | null;
  priceField: string;
  priceBreak: string | null;
  markupPercentage: string;
  discountPercentage: string;
};

type PaymentInteraction = {
  id: number;
  type: PaymentStatus;
  date: string;
};

export type PaymentRefund<T extends PaymentType> = {
  id: number;
  payment: Payment<T>;
  transactionId: string;
  user: User;
  refundedAmount: any | null; // type is supposed to be "money"
  date: Date;
};

export type PaymentTerminal = {
  id: number;
  name: string;
  deviceId: string;
};

export const PAYMENT_TRANSACTION_TYPE = {
  PAYMENT: 'payment',
  REFUND: 'refund',
  VOID: 'void',
} as const;

export type PaymentTransactionType =
  (typeof PAYMENT_TRANSACTION_TYPE)[keyof typeof PAYMENT_TRANSACTION_TYPE];

export type PriceMatrix = {
  id: number;
  hours: number;
  price: number | null;
};

export enum QuoteStatuses {
  NOT_STARTED = 'Not Started',
  ADVISOR_IN_PROGRESS = 'Advisor In Progress',
  TECHNICIAN_IN_PROGRESS = 'Technician In Progress',
  PARTS_IN_PROGRESS = 'Parts In Progress',
  SENT = 'Sent',
  VIEWED = 'Viewed',
  COMPLETED = 'Completed',
  CONFIRMED = 'Confirmed',
}

export const QuotingTabs = {
  EDIT_ADDITIONAL: 'EDIT_ADDITIONAL',
  EDIT_PRE_APPROVED: 'EDIT_PRE_APPROVED',
  QUOTE_VIEW: 'QUOTE_VIEW',
  RO_DETAILS: 'RO_DETAILS',
  QUOTE_DETAILS: 'QUOTE_DETAILS',
} as const;

export type QuotingTabsType = (typeof QuotingTabs)[keyof typeof QuotingTabs];

type ReconditioningPhase = {
  id: number;
  name: string;
  color: string;
  textColor: string | null;
  sortOrder: number;
  special: boolean;
};

type RepairOrderCustomer = {
  id: number;
  repairOrder: RepairOrder;
  customer: Customer;
};

export type CustomerPhoto = {
  id: number;
  customerId: number;
  dateUploaded: string;
  uploadedBy: string | null;
  url: string;
  urlExpiresAt: string;
};

export type CustomerPhotoPage = {
  photos: CustomerPhoto[];
  /** Every non-deleted photo the customer has, across all pages. */
  total: number;
};

export enum CheckInStatus {
  NOT_STARTED = 'Not Started',
  COMPLETE = 'Complete',
}

export enum CheckOutStatus {
  NOT_STARTED = 'Not Started',
  COMPLETE = 'Complete',
  PAID = 'Paid',
  SIGNED = 'Signed',
}

export const PAYMENT_STATUSES = {
  CREATED: 'Created',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  PAID: 'Paid',
  CONFIRMED: 'Confirmed',
  REFUNDED: 'Refunded',
  REQUESTED: 'Requested',
  VOIDED: 'Voided',
} as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[keyof typeof PAYMENT_STATUSES];

export const RO_PAYMENT_TYPES = {
  SERVICE: 'service',
} as const;

export const NON_RO_PAYMENT_TYPES = {
  PARTS: 'parts',
  FINANCE: 'finance',
  SALES: 'sales',
} as const;

export const PAYMENT_TYPES = {
  ...RO_PAYMENT_TYPES,
  ...NON_RO_PAYMENT_TYPES,
} as const;

export type SalesPaymentType = typeof PAYMENT_TYPES.SALES;
export type ServicePaymentType = typeof PAYMENT_TYPES.SERVICE;
export type PartsPaymentType = typeof PAYMENT_TYPES.PARTS;
export type FinancePaymentType = typeof PAYMENT_TYPES.FINANCE;

export type RoPaymentType = (typeof RO_PAYMENT_TYPES)[keyof typeof RO_PAYMENT_TYPES];
export type NonRoPaymentType = (typeof NON_RO_PAYMENT_TYPES)[keyof typeof NON_RO_PAYMENT_TYPES];

export type PaymentType = RoPaymentType | NonRoPaymentType;

export type CommonPaymentFields = {
  amount: string;
  applicationLabel: string | null;
  cardNumber: string | null;
  cardType: string | null;
  dateCreated: string;
  dateRefunded: string | null;
  dedicatedFileName: string | null;
  dmsId: string | null;
  id: number;
  pinStatement: string | null;
  processedBy?: User | string | null;
  processingFee: number;
  processingFeePaid: number;
  referenceId: string | null;
  refundedAmount: string | null;
  repairOrderPaymentRefunds: PaymentRefund<RoPaymentType>[];
  status: PaymentStatus;
  total: string | null;
  transactionDate: string | null;
  transactionId: string;
  userSelectedTenderType: string | null;
  writebackFailure: boolean;
  writebackFailureReason?: PaymentWritebackFailureReason | null;
};

export type NonRoPaymentFields = CommonPaymentFields & {
  customer: Customer;
  description: string | null;
  dmsReferenceId?: string | null;
  linkHash: string;
  notes?: string | null;
  number: string | null;
  interactions: PaymentInteraction[];
  paymentRefunds: PaymentRefund<NonRoPaymentType>[];
  tenderType: string;
  type: NonRoPaymentType;
};

type RoPaymentFields = CommonPaymentFields & {
  dateConfirmed: string | null;
  dateDeleted: string | null;
  datePaid: string | null;
  dateSent: string | null;
  dateViewed: string | null;
  interactions: PaymentInteraction[];
  refundReferenceId: string | null;
  repairOrder: RepairOrder;
  tenderType: string | null;
};

type PartsPaymentFields = Omit<NonRoPaymentFields, 'type' | 'paymentRefunds'> & {
  type: PartsPaymentType;
  paymentRefunds: PaymentRefund<PartsPaymentType>[];
};

type FinancePaymentFields = Omit<NonRoPaymentFields, 'type' | 'paymentRefunds'> & {
  type: FinancePaymentType;
  paymentRefunds: PaymentRefund<FinancePaymentType>[];
};

type SalesPaymentFields = Omit<NonRoPaymentFields, 'type' | 'paymentRefunds'> & {
  collectionMethod: string | null;
  invoiceNumber: string | null;
  paymentRefunds: PaymentRefund<SalesPaymentType>[];
  refundDmsReferenceId: string | null;
  refundNumber: string | null;
  refundReferenceId: string | null;
  type: SalesPaymentType;
};

// prettier-ignore
type PaymentTypeMap = {
  [K in PaymentType]:
    K extends FinancePaymentType ? FinancePaymentFields :
    K extends PartsPaymentType ? PartsPaymentFields :
    K extends SalesPaymentType ? SalesPaymentFields :
    K extends ServicePaymentType ? RoPaymentFields :
    never;
};

export type Payment<T extends PaymentType> = PaymentTypeMap[T];

export type FinancePayment = Payment<FinancePaymentType>;
export type NonRoPayment = Payment<NonRoPaymentType>;
export type PartsPayment = Payment<PartsPaymentType>;
export type RoPayment = Payment<ServicePaymentType>;
export type SalesPayment = Payment<SalesPaymentType>;

export const INTERNAL_SIGNOFF_STATUSES = {
  NOT_STARTED: 'Not Started',
  ADVISOR_IN_PROGRESS: 'Advisor In Progress',
  TECH_IN_PROGRESS: 'Technician In Progress',
  COMPLETE: 'Complete',
} as const;

type InternalSignoffStatus =
  (typeof INTERNAL_SIGNOFF_STATUSES)[keyof typeof INTERNAL_SIGNOFF_STATUSES];

export const OIL_TYPES = {
  CNV: 'cnv',
  BIN: 'bin',
  SYN: 'syn',
} as const;

export type OilType = (typeof OIL_TYPES)[keyof typeof OIL_TYPES];

export const TORQUE_UNITS = {
  NM: 'Nm',
  FT_LBS: 'ft-lbs',
} as const;

export type TorqueUnit = (typeof TORQUE_UNITS)[keyof typeof TORQUE_UNITS];

export const RO_STATUSES = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
} as const;

type RoStatus = (typeof RO_STATUSES)[keyof typeof RO_STATUSES];

// ISA-3115: runtime values live in utility/constants/vehicle-constants.ts (coding-standards
// File Organization) — this derived type is the only vehicle-history symbol that belongs here.
export type DmsHistoryStatus = (typeof DMS_HISTORY_STATUSES)[keyof typeof DMS_HISTORY_STATUSES];

export type RepairOrder = {
  appointments: Appointment[];
  approvedValue: number | null;
  checkInStatus: CheckInStatus;
  checkOutStatus: CheckOutStatus;
  customerEstimate: string | null;
  dateClosed: Date | null;
  dateCreated: Date;
  dateOfSale: Date | null;
  dateQuoteInProgress: Date | null;
  dmsKey: string | null;
  finalValue: number | null;
  hasVideo: boolean;
  hours: string | null;
  id: number;
  internal: boolean;
  internalSignoff: null;
  internalSignoffId: number | null;
  internalSignoffStatus: InternalSignoffStatus | null;
  licensePlate?: string | null;
  licensePlateState?: string | null;
  linkHash: string;
  make: string | null;
  miles: number | null;
  milesOut: number | null;
  model: string | null;
  mpiStatus: (typeof MpiStatus)[keyof typeof MpiStatus];
  mpiStatusTimestamp: Date | null;
  notes: RepairOrderNote[] | null;
  number: string;
  objectNumber: string | null;
  paymentStatus: string;
  paymentStatusTimestamp: Date | null;
  pickListPartStatus: string | null;
  pickListPartStatusTimestamp: Date | null;
  pickupDate: Date | null;
  primaryAdvisor: User | null;
  primaryCustomer: Customer | null;
  primaryPartsAdvisor: User | null;
  primaryTechnician: User | null;
  /** Lightyear dispatch priority (1=Highest .. 6=Lowest); null = unset. ISA-2074 Epic 1. */
  priority: number | null;
  promisedDate: Date | null;
  quoteInProgress: User | null;
  quoteStatus: string;
  quoteStatusTimestamp: Date | null;
  reconditioningPhase: ReconditioningPhase | null;
  reconditioningPhaseTimestamp: Date | null;
  repairOrderCustomers: RepairOrderCustomer[] | null;
  repairOrderMPI: RepairOrderMpi | null;
  repairOrderMultiPointInspectionStatus: string | null;
  repairOrderMultiPointInspectionStatusTimestamp: string | null;
  repairOrderQuote: RepairOrderQuote;
  repairOrderQuoteId: number | null;
  repairOrderReconditioningPhaseInteractions: RepairOrderReconditioningPhaseInteraction[] | null;
  repairOrderReview: RepairOrderReview;
  repairOrderServicePhaseInteractions: RepairOrderServicePhaseInteraction[] | null;
  repairOrderTeams: RepairOrderTeam[] | null;
  rewardsUsed: number;
  serialNumber: string | null;
  serviceLane: string | null;
  servicePhase: ServicePhase | null;
  servicePhaseTimestamp: Date | null;
  startValue: number | null;
  status: RoStatus;
  tagId: string | null;
  // ISA-2402 story 2.5: the flat Lightyear service Team (`Express`, `Truck Lane`), unrelated to
  // `repairOrderTeams` above despite the similar name -- that field is the people roster
  // (advisor/technician/parts agent), this one is the dealer's DMS-assigned lane.
  //
  // Optional, not `string | null`: GET /api/repair-order/{id} appends the serialization group
  // that carries this key only when the `repairOrderTeams` setting is on (AC #2 -- the key is
  // ABSENT, not null, at a gate-off dealer), so the runtime value there is `undefined`.
  team?: string | null;
  transportationType: TransportationType | null;
  upgradeQueue: boolean;
  vehicleDmsId: string | null;
  videoStatus: string;
  videoStatusTimestamp: Date | null;
  vin: string | null;
  /**
   * ISA-5110: the vehicle's owner could not be updated in the DMS; shown in the RO modal until
   * someone dismisses it. Optional because the app and the API deploy separately.
   */
  vehicleOwnerTransferAlert?: boolean;
  waiter: boolean;
  waiverSignature: string | null;
  waiverVerbiage: string | null;
  year: number | null;
  // Optional fields from advisor dashboard view endpoint
  notesCount?: number;
  hasAppointment?: boolean;
  firstAppointmentId?: number | null;
  mpiRecordId?: number | null;
  quoteRecordId?: number | null;
};

export type RepairOrderInteraction = {
  id: number;
  user: User | null;
  customer: Customer | null;
  type: string;
  date: Date;
};

export type QuoteApprovalMethod =
  (typeof QUOTE_APPROVAL_METHODS)[keyof typeof QUOTE_APPROVAL_METHODS];

export type RepairOrderQuoteRecommendationApproval = {
  id: number;
  approved: boolean;
  method: QuoteApprovalMethod;
  date: string;
  user: User | null;
  customer: Customer | null;
  operationCode: string | null;
  complaint: string | null;
  price: number | null;
  noCharge: boolean;
  signature: string | null;
};

export type RepairOrderInteractionsResponse = {
  repairOrderInteractions: ReadonlyArray<RepairOrderInteraction>;
  repairOrderMpiInteractions: ReadonlyArray<RepairOrderInteraction>;
  repairOrderQuoteInteractions: ReadonlyArray<RepairOrderInteraction>;
  repairOrderQuoteRecommendationApprovals: ReadonlyArray<RepairOrderQuoteRecommendationApproval>;
  repairOrderVideoInteractions: ReadonlyArray<unknown>;
  repairOrderPaymentInteractions: ReadonlyArray<unknown>;
  repairOrderReviewInteractions: ReadonlyArray<RepairOrderInteraction>;
};

type RepairOrderMpi = {
  id: number;
  repairOrder: RepairOrder;
  dateCompleted: Date | null;
  dateSent: Date | null;
  results: string | null;
  repairOrderMPIInteractions: RepairOrderMpiInteraction[] | null;
  dateViewed: Date | null;
};

type RepairOrderMpiInteraction = {
  id: number;
  user: User | null;
  customer: Customer | null;
  type: string;
  date: Date;
};

type RepairOrderNote = {
  id: number;
  note: string | null;
  dateCreated: Date;
  user: User | null;
};

export type RepairOrderQuote = {
  id: number;
  dateCreated: Date;
  dateSent: Date | null;
  dateCustomerViewed: Date | null;
  dateCompleted: Date | null;
  dateConfirmed: Date | null;
  etag: string | null;
  repairOrderQuoteRecommendations: RepairOrderQuoteRecommendation[];
  status: QuoteStatuses;
  subtotal: number | null;
  tax: number | null;
  processingFee: number | null;
  total: number | null;
  customerTotal: number | null;
  customerTotalTax: number | null;
  partsTax: number | null;
  laborTax: number | null;
  repairOrderQuoteLogs: RepairOrderQuoteLog[];
  deleted: boolean;
  supplies: number | null;
  // Excluded from the RO-list endpoint (GET /api/repair-order); present only on
  // detail/by-id responses. See ISA-4131.
  customer?: Customer | null;
  year: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  motorBaseVehicleId: string | null;
  motorSubModelId: string | null;
  motorEngineId: string | null;
  repairOrderQuoteMiscCharges: RepairOrderQuoteMiscCharge[];
  repairOrderQuoteDiscounts: RepairOrderQuoteDiscount[];
  discountAmount: number | null;
  miscChargeAmount: number | null;
  laborPrice: number | null;
  partsPrice: number | null;
  laborHours: number | null;
  approvedLaborPrice: number | null;
  approvedLaborHours: number | null;
  approvedPartsPrice: number | null;
  approvedSuppliesPrice: number | null;
  approvedTax: number | null;
  approvedTotal: number | null;
  approvedCustomerTotal: number | null;
  approvedDiscountTotal: number | null;
  approvedMiscChargesTotal: number | null;
  // these last 3 aren't in the php file from the backend so will need to confirm
  repairOrderId: number;
  totalDiscounts: number;
  totals: {
    approved: RepairOrderQuoteTotals;
    preApproved: RepairOrderQuoteTotals;
    additional: RepairOrderQuoteTotals;
    notApproved: RepairOrderQuoteTotals;
    all: RepairOrderQuoteTotals;
  };
  eTag?: string;
};

export type RepairOrderQuoteDiscount = {
  id?: number;
  name: string;
  type: DiscountType | null;
  amount: number;
  maxDiscount: string | null;
  addToEveryQuote?: boolean;
  taxable: boolean;
  repairOrderQuote: RepairOrderQuote | null;
  repairOrderQuoteRecommendation: RepairOrderQuoteRecommendation | null;
  discountAmount: number;
  deleted: boolean;
  appliesToParts: boolean;
  appliesToLabor: boolean;
};

export type CreateRepairOrderQuoteDiscount = Omit<
  RepairOrderQuoteDiscount,
  'repairOrderQuote' | 'repairOrderQuoteRecommendation' | 'discountAmount' | 'deleted'
>;

export type RepairOrderQuoteMiscCharge = {
  id?: number;
  name: string;
  type: ChargeType | null;
  amount: number;
  minCharge: string | null;
  maxCharge: string | null;
  addToEveryQuote?: boolean;
  taxable: boolean;
  chargeAmount: number;
  deleted: boolean;
  repairOrderQuote: RepairOrderQuote | null;
  repairOrderQuoteRecommendation: RepairOrderQuoteRecommendation | null;
  appliesToParts: boolean;
  appliesToLabor: boolean;
};

export type CreateRepairOrderQuoteMiscCharge = Omit<
  RepairOrderQuoteMiscCharge,
  'repairOrderQuote' | 'repairOrderQuoteRecommendation' | 'chargeAmount' | 'deleted'
>;

export type RepairOrderQuoteApprovalStatus = boolean | null;

type RepairOrderQuoteLog = {
  id: number;
  user: User | null;
  customer: Customer | null;
  date: string;
  data: string | null; // JSON
};

export type RepairOrderQuoteRecommendation = {
  id: number;
  approved: boolean | null;
  cause: string | null;
  chargeSupplies: boolean;
  complaint: string | null;
  correction: string | null;
  discountAmount: number | null;
  dmsId: string | null;
  isCustomPartsPrice: boolean;
  isCustomLaborPrice: boolean;
  isMaintenancePackage: boolean;
  // Excluded from the RO-list endpoint (GET /api/repair-order); present only on
  // detail/by-id responses. See ISA-4131.
  factoryMaintenanceSchedule?: FactoryMaintenanceSchedule | null;
  laborHours: number | null;
  laborPrice: number | null;
  // Excluded from the RO-list endpoint (GET /api/repair-order); present only on
  // detail/by-id responses. See ISA-4131.
  laborRate?: LaborRate | null;
  laborTax: number | null;
  laborTaxable: boolean | null;
  menuPricing: boolean;
  miscChargeAmount: number | null;
  motorApplicationId: number | null;
  noCharge: boolean | null;
  notes: string | null;
  operationCode: string;
  // ISA-2074 Story 1.5 (AC 1): the op code's last-observed dispatch skill, used only to pre-fill
  // the check-in "Skill (Dispatching)" dropdown. Read-only, resolved per response by the API from
  // the harvest library; null when the op code has no observed skill or the feature is off.
  observedSkill?: string | null;
  partsPrice: number | null;
  partsTax: number | null;
  partsTaxable: boolean | null;
  preApproved: boolean | null;
  repairOrderQuote: RepairOrderQuote;
  repairOrderQuoteDiscounts: RepairOrderQuoteDiscount[] | null;
  repairOrderQuoteMiscCharges: RepairOrderQuoteMiscCharge[] | null;
  repairOrderQuoteRecommendationParts: RepairOrderQuoteRecommendationPart[] | null;
  // Server-managed: set when the quote is sent to the customer. Until then the
  // recommendation is not visible in the Customer App.
  sent: boolean;
  sortOrder: number | null;
  subtotal: number | null;
  total: number | null;
  // these last 2 aren't in the php file from the backend so will need to confirm
  laborRateId?: number; // for create
  repairOrderQuoteId: number; // for create
};

export type RepairOrderQuoteRecommendationCreate = {
  approved?: boolean;
  laborRateId?: number;
  operationCodeId?: number;
  repairOrderQuoteId: number;
} & Omit<
  RepairOrderQuoteRecommendation,
  | 'approved'
  | 'discountAmount'
  | 'dmsId'
  | 'id'
  | 'laborPrice'
  | 'laborRate'
  | 'laborTax'
  | 'miscChargeAmount'
  | 'partsPrice'
  | 'partsTax'
  | 'repairOrderQuoteRecommendationParts'
  | 'repairOrderQuoteDiscounts'
  | 'repairOrderQuoteMiscCharges'
  | 'sent'
  | 'sortOrder'
  | 'repairOrderQuote'
  | 'subtotal'
>;

export type RepairOrderQuoteRecommendationPart = {
  id: number;
  repairOrderRecommendation: RepairOrderQuoteRecommendation;
  part: Part | null;
  number: string | null;
  name: string | null;
  price: number | null;
  quantity: number | null;
  totalPrice: number | null;
  bin: string | null;
  expectedArrival: string | null;
  expectedArrivalUpdatedDate: Date | null;
  ordered: boolean;
  arrived: boolean;
  partCondition: string | null;
  manufacturerCode: string | null;
  motorApplicationId: number | null;
  vendor: string | null | null;
};

export type RecommendationTotal = RepairOrderQuoteTotals & { id: number };

export type RepairOrderQuoteTotals = {
  customerSubtotal: number;
  customerTotal: number;
  customerTotalTax: number;
  discountAmount: number;
  discounts: number;
  labor: number;
  laborHours: number;
  laborTax: number;
  miscChargeAmount: number;
  miscCharges: number;
  parts: number;
  partsTax: number;
  processingFee: number;
  recommendations: RecommendationTotal[];
  subTotal: number;
  supplies: number;
  suppliesTax: number;
  tax: number;
  total: number;
};

type RepairOrderReconditioningPhaseInteraction = Record<string, never>;

type RepairOrderReview = {
  id: number;
  repairOrder: RepairOrder;
  status: string;
  dateSent: Date | null;
  dateViewed: Date | null;
  dateCompleted: Date | null;
  rating: string | null;
  platform: string | null;
};

type RepairOrderServicePhaseInteraction = Record<string, never>;

type RepairOrderTeam = {
  id: number;
  user: User | null;
  repairOrder: RepairOrder;
};

export type ServicePhase = {
  id: number;
  name: string;
  color: string;
  textColor: string | null;
  sortOrder: number;
  special: boolean;
  sendSms: boolean;
  smsMessage: string;
};

export type Sms = {
  id: number;
  user: User | null;
  customer: Customer | null;
  phone: string;
  message: string;
  incoming: boolean;
  isRead: boolean;
  date: Date;
  sid: string | null;
  status: string | null;
  mmsContent: string | null;
  type: string | null;
  automated: boolean;
  readBy: User | null;
  readDate: Date | null;
  incomingToPhone: string | null;
  from: User | null;
};

export type SmsThread = {
  id: number;
  name: string;
  phone: string;
  repairOrderID: number;
  repairOrderNumber: string;
  newestRoAdvisorId: number;
  newestRoClosedDate: string;
  newestTextId: number;
  message: string;
  date: string;
  newestOutgoingAdvisorId: number;
  newestOutgoingDate: string;
  totalResults: number;
  unread: number;
  priority: number;
};

export type TransportationType = {
  id: number;
  title: string;
  description: string | null;
  internal: boolean | null;
  dmsId: string | null;
};

export type UnreadMessageCounts = {
  internal: number;
  sms: number;
  service: number;
  sales: number;
};

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  username: string | null;
  phone: string;
  role: UserRoles;
  certification: string | null;
  experience: string | null;
  securityQuestion: string | null;
  lastLogin: Date | null;
  active: boolean;
  processRefund: boolean;
  shareRepairOrders: boolean;
  dmsId: string | null;
  externalAuthentication: boolean;
  allowEditProcessingFees: boolean;
  allowManualPayments: boolean;
  autoSendVideo: boolean;
  passwordResetRequired: boolean;
};

// TODO use this instead of UserRoles constant
export enum UserRoles {
  ADMIN = 'ROLE_ADMIN',
  FINANCE_MANAGER = 'ROLE_FINANCE_MANAGER',
  GLOBAL_PAY = 'ROLE_GLOBAL_PAY',
  PARTS_ADVISOR = 'ROLE_PARTS_ADVISOR',
  SALES_AGENT = 'ROLE_SALES_AGENT',
  SALES_MANAGER = 'ROLE_SALES_MANAGER',
  SERVICE_ADVISOR = 'ROLE_SERVICE_ADVISOR',
  SERVICE_MANAGER = 'ROLE_SERVICE_MANAGER',
  TECHNICIAN = 'ROLE_TECHNICIAN',
  NONE = '',
}

export const PaymentValidationWarningStates = [
  PAYMENT_STATUSES.PAID,
  PAYMENT_STATUSES.REFUNDED,
  PAYMENT_STATUSES.CONFIRMED,
  PAYMENT_STATUSES.VOIDED,
] as const;
export type PaymentValidationWarningStatus = (typeof PaymentValidationWarningStates)[number];

export type Media = {
  dateUploaded: string;
  id: number;
  media: string;
  note: string | null;
  type: string;
  uploadedBy: string | null;
};

export const RepairOrderMediaType = {
  image: 'IMAGE',
  video: 'VIDEO',
} as const;

type RepairOrderMediaTypeKey = keyof typeof RepairOrderMediaType;
export type RepairOrderMediaValue = (typeof RepairOrderMediaType)[RepairOrderMediaTypeKey];

export const MPI_SUBMISSION_STATE = {
  DRAFT: 'Draft',
  COMPLETE: 'Complete',
} as const;

type MpiSubmissionStateKey = keyof typeof MPI_SUBMISSION_STATE;
export type MpiSubmissionState = (typeof MPI_SUBMISSION_STATE)[MpiSubmissionStateKey];

type RepairOrderMultiPointInspectionMediaApiRes = {
  dateUploaded: string;
  id: number;
  media: string;
  note: string | null;
  readonly: boolean;
  type: RepairOrderMediaValue;
};

export type RepairOrderMultiPointInspectionApiRes = {
  concatenatingVideos: boolean;
  dateCompleted: string | null;
  id: number;
  repairOrderId: number;
  repairOrderMedia: RepairOrderMultiPointInspectionMediaApiRes[];
  results: string;
  status: MpiSubmissionState;
};

export enum GuideModes {
  NONE = 'None',
  READONLY = 'Read-only',
  ADD_TO_QUOTE = 'Add to Quote',
}

export const GuideType = {
  parts: 'parts',
  labor: 'labor',
} as const;

type GuideTypeKey = keyof typeof GuideType;
export type GuideTypeValue = (typeof GuideType)[GuideTypeKey];

export enum MpiResultsStatus {
  red = 'red',
  yellow = 'yellow',
  green = 'green',
  grey = 'grey',
}

export type MpiResultsItem = {
  name: string;
  status: MpiResultsStatus;
  value?: number | null;
  text?: string;
  itemId?: number;
  hasRange?: boolean;
  special?: boolean; // same as hasRange
};

export type MpiResultsGroup = {
  group: string;
  items: MpiResultsItem[];
  groupId?: number;
};

export type MpiResults = {
  name: string;
  presetName?: string | null;
  results: MpiResultsGroup[];
  templateId?: number;
  notes?: string;
};

export const PAY_TYPES = {
  CUSTOMER_PAY: 'Customer Pay',
  SERVICE_CONTRACT: 'Service Contract',
} as const;

export type PayTypeKey = keyof typeof PAY_TYPES;

export const DASHBOARD_NAMES = {
  ADVISOR_DASHBOARD: 'Advisor Dashboard',
  TECHNICIAN_DASHBOARD: 'Technician Dashboard',
  PARTS_DASHBOARD: 'Parts Dashboard',
} as const;

export type DashboardName = (typeof DASHBOARD_NAMES)[keyof typeof DASHBOARD_NAMES];

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export type MobileCheckinReportRecord = {
  name: string | null;
  repairOrderCount: number;
  mobileCheckIns: number;
  averageCompletionTime: string;
  signedCopiesSent: number;
};

// Mobile Check-in API response shape
// Includes standard pagination fields plus additional totals reported outside of `results`
export type MobileCheckinReportResponse = {
  results: MobileCheckinReportRecord[];
  totalRepairOrderCount: number;
  totalMobileCheckIns: number;
  totalSignedCopiesSent: number;
} & PaginatedResponse;

export type SelectOption = {
  label: string;
  value: string;
};

export type VideoTroubleshootingReportRecord = {
  technicianId: number;
  technicianName: string;
  originalHeight: number;
  lastVideoDate: string | null;
};

export type VideoTroubleshootingReportResponse = {
  results: VideoTroubleshootingReportRecord[];
  totalResults: number;
};

export type DeletedPreApproval = {
  id: number;
  name: string;
  operationCode: string;
  complaint: string;
  subtotal: string;
};
